/**
 * 앱 업데이트 게이트 ($j.F.AppGate) — 장비불러(/m) · 악덕알리미(/b) 공용
 *
 * 설치된 앱 버전이 운영 기준값(g5_apps · /adm/config 「앱 버전 · 스토어」)에 못 미치면 안내한다.
 *
 * 두 사이트가 같은 모듈을 쓴다. 마크업(.ag/.agt)도 같고 **스타일만 사이트별**이다
 * (sass/mobile/components/_app-gate.scss · sass/devil/components/_app-gate.scss).
 * 다른 것은 start() 옵션뿐 — 앱코드 · 판정 엔드포인트 · 스토어 여는 방식 · 문구.
 *
 *   unsupported (최하버전 미만) → 풀스크린 블로킹. 닫기 없음. 매 페이지 즉시 표시
 *   old         (최신버전 미만) → 하단 안내 카드. 닫을 수 있고 로그인당 1회만
 *
 * ── 조회는 로그인당 1회, 적용은 매 페이지 ────────────────────────────────────
 * 앱 버전은 세션 중에 바뀌지 않는다(업데이트하면 앱이 재시작). 그래서 판정은 로그인당 한 번만
 * 서버에 묻고 결과를 sessionStorage 에 들고 다닌다 — 모바일 전 페이지에 ajax 를 붙이지 않으려는
 * 것이다. 다만 **강제(unsupported)는 캐시된 결과로 매 페이지 다시 그린다.** 한 번 띄우고 마는
 * 게이트는 페이지만 옮기면 뚫려서 강제가 아니게 된다.
 * (loginKey 로 키를 갈라 재로그인하면 저절로 무효화된다 — LocationGate 와 같은 규칙)
 *
 * ── 앱 밖에서는 아무 것도 하지 않는다 ────────────────────────────────────────
 * 판정 기준이 '설치된 앱 버전'이라 웹/PC 에는 해당이 없다. isApp() 이 그 판정을 겸하는데,
 * 대리진입(관리자가 남의 계정) 중에도 false 라 CS 폰에 업데이트 화면이 뜨는 것도 함께 막힌다.
 * 앱마켓 심사용 계정 제외는 서버가 한다(AppVersionController).
 *
 * ── 설치 버전은 앱이 알려주면 그 값, 아니면 서버가 기기 기록에서 찾는다 ─────────
 * getVersion() 은 App.getAppVersion() 이 없는 앱(악덕알리미 전부 · 장비불러 구앱)에서 0 이다.
 * 그래도 ver=0 을 그대로 보낸다 — 서버(AppVersionService::resolveVer)가 그 회원·그 앱의
 * g5_member_devices.mobie_ver 로 판정하고, 쓴 버전을 응답 `ver` 로 돌려준다. 클라는 비교하지 않는다.
 *
 * ── 플랫폼은 보내지 않는다 ──────────────────────────────────────────────────
 * 기준값·스토어 주소는 안드로이드·아이폰이 따로지만 서버가 요청 UA 로 고른다. 이 모듈은 받은 결과만 쓴다.
 *
 */
const SESSION_KEY = 'app:ver:gate';   // sessionStorage — `{loginKey}` 로 받은 판정 결과(JSON)
const SHOWN_KEY   = 'app:ver:shown';  // sessionStorage — 안내(old)를 이미 띄운 loginKey

/**
 * 사이트별 차이 — 기본값은 장비불러(JB)
 *
 * `why` 는 "업데이트하면 무엇이 정상으로 도나"다. 앱마다 사용자가 아쉬워하는 것이 다르므로
 * 그대로 옮겨 쓰지 않는다 — 악덕알리미에는 오더가 없다.
 */
const JB = {
    app: '',                        // 빈값 = 앱이 알려준 패키지를 쓴다 (아래 _appCode)
    url: '/m/ajax/app-version',
    playstore: true,                // 네이티브 openPlaystore 를 먼저 시도한다
    storeGuide: '',                 // 있으면 웹뷰를 스토어로 넘기지 않는다 (아래 _toStore)
    storeMinVer: 0,                 // storeGuide 앱에서 스토어 버튼을 보여 줄 최소 버전 (아래 _canOpenStore)
    why: '업데이트해야 오더 알림을 정상으로 받을 수 있습니다.',
};

export default class AppGate {

    /* ───────────────── 진입 ───────────────── */

    /**
     * 게이트 시작. 대상이 아니면 조용히 빠진다.
     * 매 페이지 불리지만 서버 조회는 로그인당 1회다(그 뒤엔 세션 캐시로 판정).
     *
     * @param {Object} [opt] 사이트 설정. 악덕알리미는
     *   `{ app:'BD', url:'/b/ajax/app-version', playstore:false }` 로 부른다.
     */
    start = (opt) => {
        this.opt = Object.assign({}, JB, opt || {});
        // 앱 밖(웹·PC)·대리진입은 대상이 아니다. 웹에서는 getVersion() 이 0 이라
        // 이 가드가 없으면 '0 < 최하버전' 으로 전원이 강제 대상이 된다.
        if (!$j.F.App.isApp()) return;
        if (!(typeof MEMBER !== 'undefined' && MEMBER && MEMBER.num > 0)) return;

        const cached = this._cached();
        if (cached) { this._apply(cached); return; }

        $.ajax({
            url: this.opt.url,
            method: 'GET',
            dataType: 'json',
            data: { ver: $j.F.App.getVersion(), app: this._appCode() },   // ver 0 이면 서버가 기기 기록으로 판정
        }).done((res) => {
            if (!res || !res.result) return;
            this._cache(res);
            this._apply(res);
        });
    };

    /* ───────────────── 판정 적용 ───────────────── */

    /** 판정 결과대로 화면을 그린다 */
    _apply = (res) => {
        if (res.state === 'unsupported') { this._openBlocking(res); return; }
        if (res.state === 'old')         { this._openNotice(res); }
        // '' 은 판정 없음 — 기준값 미설정(0) 이거나 제외 대상. 아무 것도 하지 않는다
    };

    /* ───────────────── 강제 — 풀스크린 블로킹 ───────────────── */

    /**
     * 닫을 수 없는 전체 화면. 스토어로 보내는 버튼 하나뿐이다.
     * 스토어 주소가 등록돼 있지 않으면 버튼이 갈 곳이 없으므로 **띄우지 않는다** —
     * 나갈 길 없는 화면에 사용자를 가두는 것이 업데이트를 늦추는 것보다 나쁘다.
     */
    _openBlocking = (res) => {
        if (!res.store) return;
        if ($('#appGate').length) return;   // 이미 떠 있다

        const $el = $(`
            <div id="appGate" class="ag" role="dialog" aria-modal="true" aria-labelledby="agTitle">
                <div class="ag-box">
                    <div class="ag-ico" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                             stroke-linecap="round" stroke-linejoin="round">
                            <path d="M12 3v12"/><path d="M7 8l5-5 5 5"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>
                        </svg>
                    </div>
                    <h2 class="ag-title" id="agTitle">앱을 업데이트해 주세요</h2>
                    <p class="ag-desc">
                        지금 설치된 버전은 더 이상 지원하지 않습니다.<br>
                        ${this.opt.why}
                    </p>
                    <button type="button" class="ag-btn">업데이트하기</button>
                    <p class="ag-ver">설치 ${this._verText(res)} · 최신 ${res.latest || '-'}</p>
                </div>
            </div>
        `);

        $el.on('click', '.ag-btn', () => this._toStore(res.store, $el));
        if (!this._canOpenStore(res)) this._showStoreGuide($el);
        $('body').append($el).addClass('ag-lock');
    };

    /* ───────────────── 안내 — 비블로킹 ───────────────── */

    /** 하단 카드. 닫을 수 있고 로그인당 1회만 뜬다. */
    _openNotice = (res) => {
        const key = this._loginKey() + ':' + this._appCode();   // 앱별로 따로 (같은 도메인·같은 세션스토리지)
        if ($j.Common.Store.session.get(SHOWN_KEY) === key) return;
        $j.Common.Store.session.set(SHOWN_KEY, key);

        if (!res.store) return;             // 보낼 곳이 없으면 안내할 것도 없다
        if ($('#appTip').length) return;

        const $el = $(`
            <div id="appTip" class="agt" role="status">
                <div class="agt-txt">
                    <b>새 버전이 나왔습니다</b>
                    <span>설치 ${this._verText(res)} · 최신 ${res.latest || '-'}</span>
                </div>
                <button type="button" class="agt-go">업데이트</button>
                <button type="button" class="agt-x" aria-label="닫기">&times;</button>
            </div>
        `);

        $el.on('click', '.agt-go', () => this._toStore(res.store, $el));
        $el.on('click', '.agt-x', () => $el.remove());
        if (!this._canOpenStore(res)) this._showStoreGuide($el);
        $('body').append($el);
    };

    /* ───────────────── 스토어 이동 ───────────────── */

    /**
     * 신앱은 네이티브가 스토어를 연다. 구앱은 브로커가 없어 false 를 반환하므로 등록된 주소로
     * 직접 이동한다 — **업데이트가 필요한 쪽이 곧 구앱이라 이 폴백이 실질 경로다.**
     *
     * ⚠ 악덕알리미(BD)는 `playstore:false` 로 이 시도를 건너뛴다. 원스토어로 배포하는 앱인데
     *   `openPlaystore` 는 이름 그대로 **플레이스토어 전용** 액션이라, 수락되면 엉뚱한 스토어가
     *   열리고 거기엔 그 앱이 없다.
     *
     * ⚠ `storeGuide` 를 준 앱은 웹뷰를 스토어로 넘기지 않는다. 원스토어 웹은 곧바로 onestore:// 로
     *   넘어가는데 그 주소를 못 여는 웹뷰에는 ERR_UNKNOWN_URL_SCHEME 화면만 남는다. 앱이 `openStore` 로
     *   열어 주면 끝이고, 그 액션을 모르는 구앱은 **이 자리에서 앱을 고칠 수 없으므로** 문구로 안내한다.
     */
    _toStore = (url, $gate) => {
        if (this.opt.playstore && $j.F.App.openPlaystore()) return;
        if (this.opt.storeGuide) {
            if (!$j.F.App.openStore(url)) this._showStoreGuide($gate);
            return;
        }
        location.href = url;
    };

    /**
     * 스토어 버튼을 보여 줘도 되는 버전인가. storeGuide 를 주지 않은 앱은 늘 true.
     * 버전을 모르면 false — 버튼을 눌러도 앱이 못 여는 쪽을 먼저 막는다.
     */
    _canOpenStore = (res) => {
        if (!this.opt.storeGuide) return true;
        const own = parseFloat($j.F.App.getVersion());
        const ver = own > 0 ? own : parseFloat(res && res.ver);
        return ver >= this.opt.storeMinVer;
    };

    /**
     * 게이트 자리에 안내 문구를 넣고 눌러도 소용없는 버튼을 뺀다. 확인창을 띄우지 않는다 —
     * 강제 화면(.ag)이 최상위 슬롯이라 확인창이 그 뒤에 깔린다.
     */
    _showStoreGuide = ($gate) => {
        $gate.find('.ag-desc, .agt-txt span').text(this.opt.storeGuide);
        $gate.find('.ag-btn, .agt-go').remove();
    };

    /**
     * 서버에 보낼 앱코드.
     *   BD  : 'BD' 고정 (그 웹뷰는 악덕알리미 전용이라 물어볼 게 없다)
     *   JB  : 앱이 알려준 패키지. 모르면 **빈값** — 'JB' 를 보내면 머큐리 구앱이 장비불러
     *         기준으로 판정된다(getPackage() 는 모르면 'JB' 를 준다). 빈값이면 서버가 쿠키로 떨어진다.
     */
    _appCode = () => {
        if (this.opt.app) return this.opt.app;
        return $j.F.App.hasPackage() ? $j.F.App.getPackage() : '';
    };

    /* ───────────────── 세션 캐시 ───────────────── */

    /**
     * 이번 로그인·이 앱의 판정 결과 (없거나 다른 로그인/앱이면 null)
     *
     * 앱코드까지 비교한다 — /m 과 /b 는 같은 도메인이라 sessionStorage 를 공유한다.
     * 장비불러 판정이 악덕알리미 화면에 그대로 적용되면 엉뚱한 앱을 막는다.
     */
    _cached = () => {
        try {
            const raw = $j.Common.Store.session.get(SESSION_KEY);
            if (!raw) return null;
            const o = JSON.parse(raw);
            return (o && o.lk === this._loginKey() && o.app === this._appCode()) ? o : null;
        } catch (e) { return null; }
    };

    _cache = (res) => {
        $j.Common.Store.session.set(SESSION_KEY, JSON.stringify({
            lk: this._loginKey(), app: this._appCode(),
            state: res.state, latest: res.latest, store: res.store, ver: res.ver,
        }));
    };

    /** loginKey 없으면(구앱 세션) 회원번호로 폴백 — 최소한 탭 세션 1회로는 묶인다 */
    _loginKey = () => String((MEMBER && (MEMBER.loginKey || MEMBER.num)) || '');

    /**
     * 설치 버전 표기 — 앱이 알려준 값, 없으면 서버가 기기 기록에서 찾아 돌려준 값(res.ver).
     * 둘 다 0 이면 숫자를 보여주지 않는다.
     */
    _verText = (res) => {
        const own = $j.F.App.getVersion();
        const v = (own && parseFloat(own) > 0) ? own
                : ((res && res.ver && parseFloat(res.ver) > 0) ? res.ver : 0);
        return v ? ('V.' + v) : '알 수 없음';
    };
}
